<template>
  <DashboardLayout pageTitle="Patients">
    <div class="max-w-[1200px] mx-auto w-full">
      
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 class="text-pharma-text-main text-2xl font-black tracking-tight leading-tight">Patient Management</h2>
          <p class="text-pharma-text-muted text-sm mt-1 font-medium leading-none">Manage and monitor records for all registered patients.</p>
        </div>
        <div class="flex gap-2">
          <button 
            @click="exportToExcel" 
            :disabled="exporting"
            class="flex items-center justify-center rounded-lg h-10 px-5 bg-white border border-gray-200 text-pharma-text-main text-sm font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <span v-if="exporting" class="material-symbols-outlined mr-2 text-[20px] animate-spin">progress_activity</span>
            <span v-else class="material-symbols-outlined mr-2 text-[20px]">description</span>
            {{ exporting ? 'Exporting...' : 'Export CSV' }}
          </button>
          <button 
            @click="showRegisterModal = true" 
            class="flex items-center justify-center rounded-lg h-10 px-5 bg-primary text-white text-sm font-bold hover:bg-primary/90 shadow-lg shadow-primary/10 transition-all active:scale-95"
          >
            <span class="material-symbols-outlined mr-2 text-[20px]">person_add</span>
            Register Patient
          </button>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
        <div class="flex items-stretch rounded-lg h-11 border border-gray-200 focus-within:border-primary transition-all relative w-full bg-white">
          <div class="text-pharma-text-muted flex items-center justify-center pl-3">
            <span class="material-symbols-outlined text-[22px]">search</span>
          </div>
          <input 
            v-model="searchQuery"
            class="w-full border-none bg-transparent focus:ring-0 px-3 text-sm text-pharma-text-main placeholder:text-pharma-text-muted font-medium" 
            placeholder="Search by Email or City..."
          />
          <button 
            v-if="searchQuery" 
            @click="searchQuery = ''"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pharma-text-main transition-colors"
          >
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-h-[400px]">
        <table class="w-full text-left border-collapse table-fixed">
          <thead>
            <tr class="bg-gray-50/50 border-b border-gray-200">
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[350px]">Patient Email</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[250px]">City</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[200px]">Registered Date</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted text-right w-[150px]">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <template v-if="loading">
              <tr v-for="i in 8" :key="'skeleton-' + i" class="animate-pulse">
                <td class="px-6 py-5"><div class="h-4 bg-gray-100 rounded-md w-3/4"></div></td>
                <td class="px-6 py-5"><div class="h-4 bg-gray-50 rounded-md w-1/2"></div></td>
                <td class="px-6 py-5"><div class="h-4 bg-gray-50 rounded-md w-2/3"></div></td>
                <td class="px-6 py-5 text-right"><div class="h-4 bg-gray-50 rounded-md w-16 ml-auto"></div></td>
              </tr>
            </template>

            <template v-else>
              <tr v-for="p in patients" :key="p.id" class="hover:bg-gray-50 transition-colors group">
                <td class="px-6 py-4">
                  <span class="text-sm font-bold text-pharma-text-main tracking-tight">{{ p.email }}</span>
                </td>
                <td class="px-6 py-4">
                  <p class="text-sm text-pharma-text-muted font-medium">{{ p.city || 'N/A' }}</p>
                </td>
                <td class="px-6 py-4">
                  <p class="text-sm text-pharma-text-muted font-medium">{{ p.registered_date }}</p>
                </td>
                <td class="px-6 py-4 text-right">
                  <router-link 
                    :to="{ name: 'PatientProfile', params: { id: p.id } }" 
                    class="text-primary hover:underline font-bold text-sm transition-colors inline-block no-underline"
                  >
                    View Details
                  </router-link>
                </td>
              </tr>

              <tr v-if="patients.length === 0">
                <td colspan="4" class="px-6 py-20 text-center text-pharma-text-muted italic font-medium">
                  No patient records found matching your search.
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div class="bg-white px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <p class="text-sm text-pharma-text-muted font-medium italic">
            Showing {{ pagination.from || 0 }} - {{ pagination.to || 0 }} of {{ pagination.total }} patients
          </p>
          <div class="flex gap-2">
            <button 
              @click="fetchPatients(pagination.current_page - 1)"
              :disabled="pagination.current_page === 1 || loading"
              class="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-30 transition-colors"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <div class="h-9 px-4 flex items-center justify-center rounded-lg bg-primary text-white text-sm font-bold">
              {{ pagination.current_page }}
            </div>
            <button 
              @click="fetchPatients(pagination.current_page + 1)"
              :disabled="pagination.current_page === pagination.last_page || loading"
              class="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-30 transition-colors"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showRegisterModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300" @click="showRegisterModal = false"></div>
        <div class="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in duration-300">
          <div class="p-8">
            <h3 class="text-2xl font-black text-pharma-text-main text-center mb-8 tracking-tight uppercase">Register New Patient</h3>
            
            <div v-if="errorLabel" class="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold border border-red-100 flex items-center gap-2">
              <span class="material-symbols-outlined text-[16px]">error</span>
              {{ errorLabel }}
            </div>
            <div v-if="successLabel" class="mb-4 p-3 bg-green-50 text-green-600 rounded-xl text-xs font-bold border border-green-100 flex items-center gap-2">
              <span class="material-symbols-outlined text-[16px]">check_circle</span>
              {{ successLabel }}
            </div>

            <div class="space-y-4">
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Email Address</label>
                <input v-model="form.email" type="email" placeholder="patient@example.com" class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none text-sm font-medium bg-[#f8f9fb]" />
              </div>
              
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Password</label>
                <div class="flex items-center rounded-xl border border-gray-200 focus-within:border-primary transition-all bg-[#f8f9fb] h-12 overflow-hidden">
                  <input v-model="form.password" :type="showPass ? 'text' : 'password'" placeholder="••••••••" class="flex-1 h-full px-4 bg-transparent outline-none text-sm font-medium text-pharma-text-main" />
                  <button @click="showPass = !showPass" class="w-12 h-full flex items-center justify-center text-pharma-text-muted hover:text-pharma-text-main">
                    <span class="material-symbols-outlined text-[22px]">{{ showPass ? 'visibility' : 'visibility_off' }}</span>
                  </button>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Confirm Password</label>
                <div class="flex items-center rounded-xl border border-gray-200 focus-within:border-primary transition-all bg-[#f8f9fb] h-12 overflow-hidden">
                  <input v-model="form.password_confirmation" :type="showConfirm ? 'text' : 'password'" placeholder="••••••••" class="flex-1 h-full px-4 bg-transparent outline-none text-sm font-medium text-pharma-text-main" />
                  <button @click="showConfirm = !showConfirm" class="w-12 h-full flex items-center justify-center text-pharma-text-muted hover:text-pharma-text-main">
                    <span class="material-symbols-outlined text-[22px]">{{ showConfirm ? 'visibility' : 'visibility_off' }}</span>
                  </button>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">City (Optional)</label>
                <input v-model="form.city" type="text" placeholder="e.g. New York" class="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none text-sm font-medium bg-[#f8f9fb]" />
              </div>

              <div class="pt-4 flex gap-3">
                <button @click="showRegisterModal = false" class="flex-1 h-12 bg-gray-100 font-bold rounded-xl hover:bg-gray-200 transition">Cancel</button>
                <button 
                  @click="submitRegister" 
                  :disabled="submitting"
                  class="flex-1 h-12 bg-primary text-white font-bold rounded-xl shadow-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                >
                  <span v-if="submitting" class="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
                  <span>{{ submitting ? 'Registering...' : 'Register' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './layouts/DashboardLayout.vue'
import api from '@/services/api'

export default {
  name: 'PatientManagement',
  components: { DashboardLayout },
  data() {
    return {
      loading: false,
      searchQuery: '',
      submitting: false,
      debounceTimer: null,
      showRegisterModal: false,
      showPass: false,
      showConfirm: false,
      errorLabel: '',
      successLabel: '',
      form: { email: '', password: '', password_confirmation: '', city: '' },
      patients: [],
      exporting: false,
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0
      }
    }
  },
  watch: {
    searchQuery() {
      clearTimeout(this.debounceTimer);
      this.loading = true;
      this.debounceTimer = setTimeout(() => {
        this.fetchPatients(1);
      }, 500);
    },
    showRegisterModal(val) {
      if (!val) {
        this.resetForm();
        this.errorLabel = '';
        this.successLabel = '';
      }
    }
  },
  methods: {
    async fetchPatients(page = 1) {
      this.loading = true;
      try {
        const response = await api.get('/patients', {
          params: { 
            search: this.searchQuery,
            page: page 
          }
        });
        const { data, current_page, last_page, total, from, to } = response.data;
        this.patients = data;
        this.pagination = { current_page, last_page, total, from, to };
      } catch (error) {
        console.error("Failed to fetch patients:", error);
      } finally {
        this.loading = false;
      }
    },
    async submitRegister() {
      this.errorLabel = '';
      this.successLabel = '';
      if (!this.form.email || !this.form.password) {
        this.errorLabel = "Email and Password are required.";
        return;
      }
      if (this.form.password !== this.form.password_confirmation) {
        this.errorLabel = "Passwords do not match.";
        return;
      }
      this.submitting = true;
      try {
        const response = await api.post('/patients/register', this.form);
        this.successLabel = response.data.message || "Patient registered successfully!";
        setTimeout(() => {
          this.showRegisterModal = false;
          this.fetchPatients(this.pagination.current_page);
        }, 1500);
      } catch (error) {
        const errorData = error.response?.data;
        this.errorLabel = errorData?.errors ? Object.values(errorData.errors)[0][0] : "Registration failed.";
      } finally {
        this.submitting = false;
      }
    },
    async exportToExcel() {
      this.exporting = true;
      try {
        const response = await api.get('/patients/export', { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `Patients_Report_${date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        alert("Could not generate Excel file.");
      } finally {
        this.exporting = false;
      }
    },
    resetForm() {
      this.form = { email: '', password: '', password_confirmation: '', city: '' };
      this.showPass = false;
      this.showConfirm = false;
    }
  },
  mounted() {
    this.fetchPatients();
  }
}
</script>